import { Link } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { Mail, Phone, MapPin } from 'lucide-react';
import SocialButton from './SocialButton';

const Footer = () => {
  const { t } = useLanguage();
  const { settings } = useSiteSettings();
  const show = settings.show_contact_in_footer;
  const addressParts = [settings.contact_street, [settings.contact_postal_code, settings.contact_city].filter(Boolean).join(' '), settings.contact_country].filter(Boolean);
  const formattedAddress = addressParts.join(', ');
  const hasContactInfo = show && (settings.contact_email || settings.contact_phone || formattedAddress);

  const instagramUrl = settings.social_instagram || 'https://www.instagram.com/szoradi.instruments/';
  const facebookUrl = settings.social_facebook || 'https://www.facebook.com/Szoradi.Instruments/';

  // Copyright text from DB with {year} replacement
  const copyrightText = (settings.footer_copyright_text || '© {year} Szoradi Instruments')
    .replace('{year}', new Date().getFullYear().toString());

  return (
    <footer className="border-t border-gold/15 py-16 px-6" role="contentinfo">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col items-center text-center md:text-left md:flex-row md:justify-between md:items-start gap-8 md:gap-10">
          {/* Left: Brand + copyright */}
          <div>
            <p className="font-serif text-lg text-foreground mb-2">Szoradi Instruments</p>
            <p className="text-xs tracking-widest uppercase text-muted-foreground/60 font-sans">
              {copyrightText}
            </p>
          </div>

          {/* Center: Contact info */}
          {hasContactInfo && (
            <div className="flex flex-col gap-2 text-xs text-muted-foreground">
              {settings.contact_email && (
                <a href={`mailto:${settings.contact_email}`} className="flex items-center gap-2 hover:text-foreground transition-colors">
                  <Mail className="w-3 h-3" /> {settings.contact_email}
                </a>
              )}
              {settings.contact_phone && (
                <a href={`tel:${settings.contact_phone.replace(/\s/g, '')}`} className="flex items-center gap-2 hover:text-foreground transition-colors">
                  <Phone className="w-3 h-3" /> {settings.contact_phone}
                </a>
              )}
              {formattedAddress && (
                <span className="flex items-center gap-2">
                  <MapPin className="w-3 h-3 flex-shrink-0" /> {formattedAddress}
                </span>
              )}
            </div>
          )}

          {/* Right: Social links */}
          <div className="flex items-center gap-5">
            {instagramUrl && <SocialButton platform="instagram" href={instagramUrl} compact />}
            {facebookUrl && <SocialButton platform="facebook" href={facebookUrl} compact />}
          </div>
        </div>

        {/* Legal links */}
        {(settings.page_impressum_visible || settings.page_datenschutz_visible) && (
          <div className="mt-10 pt-6 border-t border-border/30 flex gap-6 justify-center">
            {settings.page_impressum_visible && (
              <Link to="/impressum" className="text-[10px] tracking-[0.15em] uppercase text-muted-foreground/50 hover:text-muted-foreground transition-colors">
                {t.footerImprint}
              </Link>
            )}
            {settings.page_datenschutz_visible && (
              <Link to="/datenschutz" className="text-[10px] tracking-[0.15em] uppercase text-muted-foreground/50 hover:text-muted-foreground transition-colors">
                {t.footerPrivacy}
              </Link>
            )}
          </div>
        )}
      </div>
    </footer>
  );
};

export default Footer;
